import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Key, Plus, Pencil, Trash2, Copy, Check, Shield, Calendar, FileText,
  Users, Search as SearchIcon, Mail, Settings, Tag, UserCheck, UserX, Clock
} from 'lucide-react';
import api from '../../lib/api';

const PERMISSION_LABELS = {
  documents: { label: 'Rechnungen & Angebote', icon: FileText },
  calendar: { label: 'Kalender / Termine', icon: Calendar },
  customers: { label: 'Kunden verwalten', icon: Users },
  gutachten: { label: 'Gutachten', icon: FileText },
  contracts: { label: 'Verträge', icon: FileText },
  lead_finder: { label: 'Auftrags-Finder', icon: SearchIcon },
  discount_codes: { label: 'Rabattcodes', icon: Tag },
  emails: { label: 'E-Mail-Logs', icon: Mail },
  settings: { label: 'Einstellungen', icon: Settings },
};

const DEFAULT_PERMISSIONS = Object.fromEntries(
  Object.keys(PERMISSION_LABELS).map(k => [k, false])
);

export default function AdminLicenses() {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editLicense, setEditLicense] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  // Form states
  const [formValidUntil, setFormValidUntil] = useState('');
  const [formPermissions, setFormPermissions] = useState({ ...DEFAULT_PERMISSIONS });
  const [formNotes, setFormNotes] = useState('');

  const fetchLicenses = async () => {
    try {
      const res = await api.get('/admin/licenses');
      setLicenses(res.data);
    } catch (e) {
      toast.error('Fehler beim Laden der Lizenzen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLicenses(); }, []);

  const resetForm = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    setFormValidUntil(d.toISOString().split('T')[0]);
    setFormPermissions({ ...DEFAULT_PERMISSIONS });
    setFormNotes('');
  };

  const handleCreate = async () => {
    try {
      const res = await api.post('/admin/licenses', {
        valid_until: formValidUntil,
        permissions: formPermissions,
        notes: formNotes
      });
      toast.success(`Lizenz erstellt: ${res.data.license_key}`);
      setShowCreate(false);
      fetchLicenses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleUpdate = async () => {
    if (!editLicense) return;
    try {
      await api.put(`/admin/licenses/${editLicense.id}`, {
        valid_until: formValidUntil,
        permissions: formPermissions,
        notes: formNotes,
        is_active: editLicense.is_active
      });
      toast.success('Lizenz aktualisiert');
      setEditLicense(null);
      fetchLicenses();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleToggleActive = async (lic) => {
    try {
      await api.put(`/admin/licenses/${lic.id}`, { is_active: !lic.is_active });
      toast.success(lic.is_active ? 'Lizenz deaktiviert' : 'Lizenz aktiviert');
      fetchLicenses();
    } catch (e) {
      toast.error('Fehler');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/licenses/${deleteId}`);
      toast.success('Lizenz gelöscht');
      setDeleteId(null);
      fetchLicenses();
    } catch (e) {
      toast.error('Fehler beim Löschen');
    }
  };

  const copyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      setCopiedKey(key);
      toast.success('Lizenzschlüssel kopiert');
      setTimeout(() => setCopiedKey(null), 2000);
    } catch { toast.error('Kopieren fehlgeschlagen'); }
  };

  const isExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE');
  };

  const activePerms = (perms) => {
    if (!perms) return [];
    return Object.entries(perms).filter(([, v]) => v).map(([k]) => PERMISSION_LABELS[k]?.label || k);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Lade Lizenzen...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-gray-900">Lizenzverwaltung</h1>
          <p className="font-manrope text-sm text-gray-500 mt-1">Erstellen und verwalten Sie Lizenzen für Benutzer</p>
        </div>
        <Button onClick={() => { resetForm(); setShowCreate(true); }} className="bg-[#9B1B30] hover:bg-[#7d1526]" data-testid="create-license-btn">
          <Plus className="w-4 h-4 mr-2" /> Neue Lizenz
        </Button>
      </div>

      {licenses.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500">
          <Key className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-manrope">Noch keine Lizenzen erstellt</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {licenses.map(lic => (
            <Card key={lic.id} className={`border-l-4 ${!lic.is_active ? 'border-l-gray-300 opacity-60' : isExpired(lic.valid_until) ? 'border-l-red-400' : 'border-l-green-500'}`} data-testid={`license-card-${lic.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="font-mono font-bold text-lg text-[#9B1B30]">{lic.license_key}</code>
                      <button onClick={() => copyKey(lic.license_key)} className="p-1 rounded border hover:bg-gray-50" data-testid={`copy-license-${lic.id}`}>
                        {copiedKey === lic.license_key ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                      </button>
                      {!lic.is_active && <Badge variant="secondary">Deaktiviert</Badge>}
                      {lic.is_active && isExpired(lic.valid_until) && <Badge variant="destructive">Abgelaufen</Badge>}
                      {lic.is_active && !isExpired(lic.valid_until) && <Badge className="bg-green-100 text-green-800">Aktiv</Badge>}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Gültig bis: {formatDate(lic.valid_until)}</span>
                      {lic.assigned_email ? (
                        <span className="flex items-center gap-1"><UserCheck className="w-3.5 h-3.5 text-green-600" /> {lic.assigned_email}</span>
                      ) : (
                        <span className="flex items-center gap-1"><UserX className="w-3.5 h-3.5 text-gray-400" /> Nicht zugewiesen</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {activePerms(lic.permissions).map(p => (
                        <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                      ))}
                      {activePerms(lic.permissions).length === 0 && <span className="text-xs text-gray-400">Keine Berechtigungen</span>}
                    </div>
                    {lic.notes && <p className="text-xs text-gray-400 italic">{lic.notes}</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => handleToggleActive(lic)} data-testid={`toggle-license-${lic.id}`}>
                      {lic.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => {
                      setFormValidUntil(lic.valid_until?.split('T')[0] || '');
                      setFormPermissions(lic.permissions || { ...DEFAULT_PERMISSIONS });
                      setFormNotes(lic.notes || '');
                      setEditLicense(lic);
                    }} data-testid={`edit-license-${lic.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setDeleteId(lic.id)} data-testid={`delete-license-${lic.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showCreate || !!editLicense} onOpenChange={(open) => { if (!open) { setShowCreate(false); setEditLicense(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editLicense ? 'Lizenz bearbeiten' : 'Neue Lizenz erstellen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Gültig bis</Label>
              <Input type="date" value={formValidUntil} onChange={(e) => setFormValidUntil(e.target.value)} data-testid="license-valid-until" />
            </div>
            <div>
              <Label className="mb-2 block">Berechtigungen</Label>
              <div className="space-y-2">
                {Object.entries(PERMISSION_LABELS).map(([key, { label, icon: Icon }]) => (
                  <div key={key} className="flex items-center justify-between p-2 rounded border hover:bg-gray-50">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-gray-500" />
                      <span className="text-sm">{label}</span>
                    </div>
                    <Switch
                      checked={formPermissions[key] || false}
                      onCheckedChange={(checked) => setFormPermissions(prev => ({ ...prev, [key]: checked }))}
                      data-testid={`perm-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Notizen (optional)</Label>
              <Input value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="z.B. Für Mitarbeiter XY" data-testid="license-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditLicense(null); }}>Abbrechen</Button>
            <Button className="bg-[#9B1B30] hover:bg-[#7d1526]" onClick={editLicense ? handleUpdate : handleCreate} data-testid="save-license-btn">
              {editLicense ? 'Speichern' : 'Lizenz erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lizenz löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Der zugewiesene Benutzer wird deaktiviert.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
